import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, X, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import BookingSlotPicker from "@/components/BookingSlotPicker";

const PLAN_LABEL = {
  inperson_1x_monthly: "1×/wk",
  inperson_2x_monthly: "2×/wk",
  inperson_3x_monthly: "3×/wk",
  inperson_3mo_2x: "3-month · 2×/wk",
  inperson_6mo_2x: "6-month · 2×/wk",
  inperson_single: "Single session",
};

const fmt = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function SessionsCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBook, setShowBook] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sessions/summary");
      setSummary(r.data);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !summary || (!summary.has_inperson_plan && !summary.can_book)) return null;

  const credits = summary.booking_mode !== "open"; // in-person plan with per-cycle credits

  const cancel = async (id) => {
    const warn = credits
      ? "Cancel this session? Heads up — cancellations do NOT come with a refund. The session still counts against your package."
      : "Cancel this session?";
    if (!window.confirm(warn)) {
      return;
    }
    try {
      await api.post(`/sessions/${id}/cancel`);
      toast.success("Session cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <div className="obs-card" data-testid="sessions-card">
      <div className="flex items-center gap-3 mb-3">
        <MapPin className="h-5 w-5 text-blaze" />
        <div className="flex-1">
          <div className="overline">{credits ? "In-person training" : "Training sessions"}</div>
          <h3 className="font-heading text-2xl uppercase">Book my sessions</h3>
        </div>
        <button
          type="button"
          onClick={() => setShowBook(true)}
          disabled={credits && summary.remaining <= 0}
          data-testid="sessions-book-btn"
          className="blaze-btn px-3 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Plus className="h-3 w-3" /> Book
        </button>
      </div>

      {credits ? (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="stat-tile">
            <div className="overline">Remaining</div>
            <div className="font-data text-3xl text-blaze">{summary.remaining}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
              of {summary.allocation} · {PLAN_LABEL[summary.plan] || summary.plan}
            </div>
          </div>
          <div className="stat-tile">
            <div className="overline">Booked</div>
            <div className="font-data text-3xl">{summary.upcoming.length}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">upcoming</div>
          </div>
          <div className="stat-tile">
            <div className="overline">Plan ends</div>
            <div className="font-data text-lg">
              {summary.expires_at ? new Date(summary.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-xs text-zinc-500 mb-4" data-testid="sessions-open-note">
          Your online plan includes session booking — grab an open slot with your coach for an{" "}
          <span className="text-zinc-300">online video call</span> or an{" "}
          <span className="text-zinc-300">in-person session</span>.
        </p>
      )}

      {summary.upcoming.length > 0 ? (
        <div className="space-y-2">
          <div className="overline text-zinc-500">/ Upcoming</div>
          {summary.upcoming.map((s) => (
            <div
              key={s.id}
              data-testid={`session-row-${s.id}`}
              className="flex items-center gap-3 p-2 border border-obsidian-600 rounded-sm"
            >
              <Clock className="h-4 w-4 text-blaze" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 flex items-center gap-2">
                  <span>{fmt(s.scheduled_at)}</span>
                  <span
                    className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                      s.session_type === "online"
                        ? "border-sky-500/50 text-sky-400"
                        : "border-blaze/50 text-blaze"
                    }`}
                  >
                    {s.session_type === "online" ? "Online" : "In person"}
                  </span>
                </div>
                {s.note && <div className="text-[11px] text-zinc-500 truncate">{s.note}</div>}
              </div>
              <button
                type="button"
                onClick={() => cancel(s.id)}
                data-testid={`session-cancel-${s.id}`}
                className="ghost-btn px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                title="Cancels the session — no refund"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-zinc-500">
          No sessions booked yet. Tap <span className="text-zinc-200">Book</span> above to schedule your first one.
        </p>
      )}

      {summary.past.length > 0 && (
        <details className="mt-3">
          <summary className="text-[10px] text-zinc-500 uppercase tracking-widest cursor-pointer hover:text-zinc-300">
            Past & cancelled ({summary.past.length})
          </summary>
          <div className="mt-2 space-y-1">
            {summary.past.slice(0, 6).map((s) => (
              <div key={s.id} className="text-[11px] text-zinc-500 flex items-center gap-2">
                <span className="w-24">{fmt(s.scheduled_at)}</span>
                <span className={s.status === "cancelled" ? "text-red-400/70" : "text-zinc-400"}>
                  {s.status}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {showBook && (
        <BookingSlotPicker
          onClose={() => setShowBook(false)}
          onBooked={() => {
            setShowBook(false);
            load();
          }}
        />
      )}
    </div>
  );
}
